'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createClient } from '../lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const PFX = 'sb_';

function clearStaleTokens() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX) || k.startsWith('sb-'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('sb-')) {
        document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
      }
    });
  } catch {}
}

const STALE_TOKEN_CODES = new Set([
  'refresh_token_not_found',
  'refresh_token_already_used',
  'over_request_rate_limit',
]);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Memoize client so it's created only once per provider mount
  const supabase = useMemo(() => createClient(), []);

  // Guard against running the effect more than once
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    const fetchSession = async (attempt = 0) => {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        const code = (error as any).code as string | undefined;

        if (code === 'over_request_rate_limit') {
          // Exponential backoff: 2s, 4s, 8s — max 3 retries
          if (attempt < 3) {
            const delay = Math.pow(2, attempt + 1) * 1000;
            retryTimeout = setTimeout(() => fetchSession(attempt + 1), delay);
            return;
          }
          // After retries exhausted, treat as unauthenticated
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        if (code && STALE_TOKEN_CODES.has(code)) {
          clearStaleTokens();
          setSession(null);
          setUser(null);
        } else {
          setSession(null);
          setUser(null);
        }
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
      setLoading(false);
    };

    fetchSession();

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        event === 'SIGNED_OUT' ||
        (event === 'TOKEN_REFRESHED' && !session) ||
        event === 'USER_DELETED'
      ) {
        clearStaleTokens();
        setSession(null);
        setUser(null);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [supabase]);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    clearStaleTokens();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
